<template>
    <div class="quote-chat-conversation d-flex flex-column">
        <div ref="scrollcontainerRef" class="messages-list">
            <div>
                <chat-message
                    v-for="msg in chatMessages"
                    :key="msg.id"
                    class="my-3"
                    :senderName="msg.sender.name"
                    :senderAvatar="msg.sender.avatar"
                    :senderIsSelf="msg.sender.is_customer === isCustomer"
                    :message="
                        isCustomer && !msg.sender.is_customer
                            ? msg.parsed_message
                            : msg.message
                    "
                    :quote-key="quoteKey"
                    :is-customer="isCustomer"
                    :disable-pdf-viewer="disablePdfViewer"
                    :attachment="msg.attachment"
                    :fromAI="Boolean(!msg.sender.is_customer && msg.uses_ai)"
                    :timestamp="isCustomer ? null : new Date(msg.created_at)"
                ></chat-message>
            </div>
        </div>
        <chat-form
            :chat-available-for-customer="chatAvailableForCustomer"
            :chat-attachments-available="chatAttachmentsAvailable"
            :attachment-accept="attachmentAccept"
            :chat-response-time-text="chatResponseTimeText"
            :quote-key="quoteKey"
            :is-customer="isCustomer"
            :is-template="isTemplate"
            :seller="seller"
            :reply-as-enabled="replyAsEnabled"
            :reply-as-options="replyAsOptions"
            :reply-as-default-user-id="replyAsDefaultUserId"
            @submitted="showSentMessage"
        ></chat-form>
    </div>
</template>

<style lang="scss" scoped>
.quote-chat-conversation {
    min-height: 0;

    > form {
        flex-shrink: 0;
    }
}

.messages-list {
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    overflow-anchor: none;
    flex-basis: 0;
    flex-grow: 1;
    display: flex;
    flex-direction: column-reverse;
}
</style>

<script setup>
import { route } from "ziggy";
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import axios from "axios";
import ChatForm from "./form";
import ChatMessage from "./message";
const scrollcontainerRef = ref(null);
const props = defineProps({
    quoteKey: String,
    disablePdfViewer: Boolean,
    isCustomer: {
        type: Boolean,
        default: false,
    },
    isTemplate: {
        type: Boolean,
        default: false,
    },
    markAsSeen: {
        type: Boolean,
        default: false,
    },
    shouldReloadMessages: Boolean,
    chatAvailableForCustomer: {
        type: Boolean,
        default: true,
    },
    chatAttachmentsAvailable: {
        type: Boolean,
        default: false,
    },
    attachmentAccept: {
        type: String,
        default: "",
    },
    chatResponseTimeText: {
        type: String,
    },
    seller: {
        type: String,
    },
});
const chatMessages = ref(undefined);
const interval = ref(null);
const replyAsEnabled = ref(false);
const replyAsOptions = ref([]);
const replyAsDefaultUserId = ref(null);
let latestLoad = 0;
async function loadMessages() {
    const load = ++latestLoad;
    const response = await axios.get(
        route("quote.chatMessages.index", {
            quoteKey: props.quoteKey,
            is_template: props.isTemplate,
        })
    );
    if (load !== latestLoad) {
        return;
    }
    const oldCount = chatMessages.value ? chatMessages.value.length : 0;
    chatMessages.value = response.data.data.sort((a, b) =>
        a.id > b.id ? 1 : -1
    );
    if (chatMessages.value.length > oldCount) {
        await nextTick();
        scrollcontainerRef.value.scrollTop = 0;
    }
}
async function showSentMessage(message) {
    latestLoad++;
    chatMessages.value = [
        ...(chatMessages.value ?? []).filter((existing) => existing.id !== message.id),
        message,
    ].sort((a, b) => a.id - b.id);
    await nextTick();
    scrollcontainerRef.value.scrollTop = 0;
}
async function markMessagesAsSeen() {
    await axios.post(route("quote.chatMessages.markAsSeen", props.quoteKey));
}
async function loadReplyAsOptions() {
    const response = await axios.get(
        route("quote.chatMessages.replyAsOptions", {
            quoteKey: props.quoteKey,
            is_template: props.isTemplate,
        })
    );
    replyAsEnabled.value = response.data.enabled;
    replyAsOptions.value = response.data.options;
    replyAsDefaultUserId.value = response.data.defaultUserId;
}
watch(
    () => props.markAsSeen,
    async (newValue) => {
        if (newValue) {
            await markMessagesAsSeen();
        }
    }
);
onMounted(async () => {
    await loadMessages();
    if (!props.isCustomer) {
        await loadReplyAsOptions();
    }
    if (props.markAsSeen) {
        await markMessagesAsSeen();
    }
    interval.value = setInterval(async () => {
        if (props.shouldReloadMessages) {
            await loadMessages();
        }
        if (props.markAsSeen) {
            await markMessagesAsSeen();
        }
    }, 5000);
});
onUnmounted(() => {
    clearInterval(interval.value);
    interval.value = null;
});
</script>
